import React from 'react';
import {
  PhysicianPicker,
  VerbalPicker,
  FacilityPicker,
  LOCPicker,
  EffDateTimePicker,
  EffDatePicker,
  DischargePicker,
  RoleGroupPicker,
  FrequencyPicker,
  PeriodPicker,
} from '../../Components/components';
import {
  SuggestBoxWrapper,
  ErrorBoxWrapper,
  StatementText,
  OrderTypeStatement,
  OrderTypeWrapper,
} from '../../../components/layout/WrapperDiv';
import { infoWidth, boxWidth, physWidth } from '../../Components/constants';
import defaults from '../../../translations/en.json';

type OrderTypeProps = {
  orderName: string | any;
};

export const OrderType = ({ orderName }: OrderTypeProps) => (
  <OrderTypeStatement id="verify-verbal-date">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyOrderType}
    </SuggestBoxWrapper>
    <OrderTypeWrapper width={boxWidth}>{orderName}</OrderTypeWrapper>
  </OrderTypeStatement>
);

type ErrorLabelProps = {
  errorLabel: string | any;
};

export const ErrorLabel = ({ errorLabel }: ErrorLabelProps) => (
  <ErrorBoxWrapper width={boxWidth}>{errorLabel}</ErrorBoxWrapper>
);

type VerbalCheckProps = {
  verbalDate: Date | number | null;
  handleVerbalDateChange: (verbaldate: any) => void;
  severity: mumms.Severity;
  error?: string;
};

export const VerbalCheck = ({
  verbalDate,
  handleVerbalDateChange,
  severity,
  error,
}: VerbalCheckProps) => (
  <StatementText id="verify-verbal-date-check">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyVerbalReceived}
    </SuggestBoxWrapper>
    <VerbalPicker
      unsetPopperStyles
      width={195}
      selectedDate={verbalDate}
      handleVerbalDateChange={handleVerbalDateChange}
      severity={severity}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type EffDateCheckProps = {
  checkEffDate: Date | number | null;
  handleEffDateChange: (effdate: any) => void;
  severity: mumms.Severity;
  error?: string;
};

export const EffDateCheck = ({
  checkEffDate,
  handleEffDateChange,
  severity,
  error,
}: EffDateCheckProps) => (
  <StatementText id="verify-effective-date">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyOrderEffective}
    </SuggestBoxWrapper>
    <EffDatePicker
      unsetPopperStyles
      width={195}
      selectedDate={checkEffDate}
      handleEffDateChange={handleEffDateChange}
      severity={severity}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

export const EffDateTimeCheck = ({
  checkEffDate,
  handleEffDateChange,
  severity,
  error,
}: EffDateCheckProps) => (
  <StatementText id="verify-effective-date-time">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyOrderEffective}
    </SuggestBoxWrapper>
    <EffDateTimePicker
      width={195}
      unsetPopperStyles
      selectedDate={checkEffDate}
      handleEffDateChange={handleEffDateChange}
      severity={severity}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type PhysicianCheckProps =
  | {
      checkDoctor: mumms.Doctor;
      doctorsOptions: mumms.Doctor[];
      handlePhysicianChange: (doctor: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const PhysicianCheck = ({
  checkDoctor,
  doctorsOptions,
  handlePhysicianChange,
  severity,
  error,
  ...props
}: PhysicianCheckProps) => (
  <StatementText id="verify-physician">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyPhysicianOrder}
    </SuggestBoxWrapper>
    <PhysicianPicker
      doctor={checkDoctor}
      doctorsOptions={doctorsOptions}
      handlePhysicianChange={handlePhysicianChange}
      severity={severity}
      {...props}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type FrequencyCheckProps =
  | {
      checkFrequency: mumms.OptionType;
      frequencyOptions: mumms.OptionType[];
      handleFrequencyChange: (frequency: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const FrequencyCheck = ({
  checkFrequency,
  frequencyOptions,
  handleFrequencyChange,
  severity,
  error,
  ...props
}: FrequencyCheckProps) => (
  <StatementText id="verify-frequency">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyFrequency}
    </SuggestBoxWrapper>
    <FrequencyPicker
      frequency={checkFrequency}
      frequencyOptions={frequencyOptions}
      handleFrequencyChange={handleFrequencyChange}
      severity={severity}
      {...props}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type PeriodCheckProps =
  | {
      checkPeriod: mumms.OptionType;
      periodOptions: mumms.OptionType[];
      handlePeriodChange: (period: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const PeriodCheck = ({
  checkPeriod,
  periodOptions,
  handlePeriodChange,
  severity,
  error,
  ...props
}: PeriodCheckProps) => (
  <StatementText id="verify-period">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyPeriod}
    </SuggestBoxWrapper>
    <PeriodPicker
      period={checkPeriod}
      periodOptions={periodOptions}
      handlePeriodChange={handlePeriodChange}
      severity={severity}
      {...props}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type RoleGroupCheckProps =
  | {
      checkRoleGroup: mumms.RoleGroup;
      roleGroupOptions: mumms.RoleGroup[];
      handleRoleGroupChange: (roleGroup: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const RoleGroupCheck = ({
  checkRoleGroup,
  roleGroupOptions,
  handleRoleGroupChange,
  severity,
  error,
  ...props
}: RoleGroupCheckProps) => (
  <StatementText id="verify-roleGroup">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyRoleGroup}
    </SuggestBoxWrapper>
    <RoleGroupPicker
      roleGroup={checkRoleGroup}
      roleGroupOptions={roleGroupOptions}
      handleRoleGroupChange={handleRoleGroupChange}
      severity={severity}
      {...props}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type LOCCheckProps =
  | {
      checkLocType: mumms.OptionType | undefined;
      locOptions: mumms.OptionType[];
      handleLocTypeChange: (loc: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const LOCCheck = ({
  checkLocType,
  locOptions,
  handleLocTypeChange,
  severity,
  error,
  ...props
}: LOCCheckProps) => (
  <StatementText id="verify-loc">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyLocChange}
    </SuggestBoxWrapper>
    <LOCPicker
      value={checkLocType}
      options={locOptions}
      handleLocTypeChange={handleLocTypeChange}
      severity={severity}
      {...props}
      width={physWidth}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type FacilityCheckProps =
  | {
      checkFacility: mumms.OptionType | undefined;
      facilityOptions: mumms.OptionType[];
      handleFacilityChange: (fac: any) => void;
      severity: mumms.Severity;
      error?: string;
    }
  | any;

export const FacilityCheck = ({
  checkFacility,
  facilityOptions,
  handleFacilityChange,
  severity,
  error,
  ...props
}: FacilityCheckProps) => (
  <StatementText id="verify-checkFacility">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyFacilityChange}
    </SuggestBoxWrapper>
    <FacilityPicker
      value={checkFacility}
      options={facilityOptions}
      handleFacilityChange={handleFacilityChange}
      severity={severity}
      {...props}
      width={physWidth}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);

type DischargeCheckProps = {
  checkDisReason: mumms.OptionType | undefined;
  dischargeOptions: mumms.OptionType[];
  handleDischargeChange: (reason: any) => void;
  severity: mumms.Severity;
  error?: string;
};

export const DischargeCheck = ({
  checkDisReason,
  dischargeOptions,
  handleDischargeChange,
  severity,
  error,
}: DischargeCheckProps) => (
  <StatementText id="verify-discharge">
    <SuggestBoxWrapper width={infoWidth}>
      {defaults.verifyDischargeReason}
    </SuggestBoxWrapper>
    <DischargePicker
      value={checkDisReason}
      options={dischargeOptions}
      handleDischargeChange={handleDischargeChange}
      severity={severity}
    />
    {error && <ErrorLabel errorLabel={error} />}
  </StatementText>
);
